import { getErc20TokenByAddress } from '@ui/constants/tokens';
import { RequireConnectionClickable } from '@ui/features/Wallet/components/RequireConnectionClickable';
import { useDataChainId } from '@ui/hooks/datachain/useDataChainStore';
import { formatBigValue } from '@ui/lib/bignumber/bignumber';
import { Button } from '@ui/lib/park/ui/button';
import { Text } from '@ui/lib/park/ui/text';
import React, { useMemo } from 'react';
import { Box, Flex, Grid, HStack, Stack, styled } from 'styled-system/jsx';

import { Competition, CompetitionStatus } from './types';

interface CompetitionCardProps {
  competition: Competition;
  onSelect?: (competition: Competition) => void;
  onJoin?: (competition: Competition) => void;
  isConnected?: boolean;
}

const toMs = (t: number) => (t < 1_000_000_000_000 ? t * 1000 : t);

/** "06/28 00:00" (UTC) from a unix timestamp in s or ms */
export const formatUtcShort = (t: number) => {
  const iso = new Date(toMs(t)).toISOString();

  return `${iso.slice(5, 7)}/${iso.slice(8, 10)} ${iso.slice(11, 16)}`;
};

/** V2 countdown — inline mono `dd:hh:mm:ss` (same math as the old block countdown) */
export const CountdownInline = ({
  targetTime,
  doneLabel,
}: {
  targetTime: number;
  doneLabel: string;
}) => {
  const targetTimeMs = toMs(targetTime);
  const [timeLeft, setTimeLeft] = React.useState(
    Math.max(0, targetTimeMs - Date.now()),
  );

  React.useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(Math.max(0, targetTimeMs - Date.now()));
    }, 1000);

    return () => clearInterval(timer);
  }, [targetTimeMs]);

  if (timeLeft === 0) return <>{doneLabel}</>;

  const days = Math.floor(timeLeft / (1000 * 60 * 60 * 24));
  const hours = Math.floor(
    (timeLeft % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60),
  );
  const minutes = Math.floor((timeLeft % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((timeLeft % (1000 * 60)) / 1000);

  const pad2 = (n: number) => n.toString().padStart(2, '0');

  return (
    <>{`${pad2(days)}:${pad2(hours)}:${pad2(minutes)}:${pad2(seconds)}`}</>
  );
};

/** Status pill — LIVE (long, pulsing dot) / UPCOMING (neutral) / ENDED (muted) */
export const StatusPill = ({ status }: { status: CompetitionStatus }) => {
  if (status === CompetitionStatus.ONGOING) {
    return (
      <Flex
        align="center"
        gap="6px"
        h="22px"
        px="8px"
        borderRadius="2px"
        bg="v2.long.dim"
        border="1px solid"
        borderColor="v2.long.border"
        flexShrink={0}
      >
        <Box
          w="6px"
          h="6px"
          borderRadius="full"
          bg="v2.long.default"
          animation="twinkle 1.6s ease-in-out infinite"
        />
        <Text
          as="span"
          fontFamily="Inter"
          fontSize="10px"
          fontWeight={600}
          letterSpacing="0.06em"
          lineHeight="1"
          color="v2.long.default"
        >
          LIVE
        </Text>
      </Flex>
    );
  }

  const isEnded = status === CompetitionStatus.ENDED;

  return (
    <Flex
      align="center"
      h="22px"
      px="8px"
      borderRadius="2px"
      bg={isEnded ? 'v2.bg.surface1' : 'v2.bg.surface2'}
      border="1px solid"
      borderColor="v2.border.default"
      flexShrink={0}
    >
      <Text
        as="span"
        fontFamily="Inter"
        fontSize="10px"
        fontWeight={600}
        letterSpacing="0.06em"
        lineHeight="1"
        color={isEnded ? 'v2.text.subtle' : 'v2.text.secondary'}
      >
        {isEnded ? 'ENDED' : 'UPCOMING'}
      </Text>
    </Flex>
  );
};

/** kind meta next to the status pill (competition type) */
const KindMeta = ({ children }: { children: React.ReactNode }) => (
  <Text as="span" textStyle="v2.sectionLabel" color="v2.text.subtle">
    {children}
  </Text>
);

/** Featured-card stat cell: micro label over mono value, optional meta line */
const StatCell = ({
  label,
  children,
  meta,
  accent,
}: {
  label: string;
  children: React.ReactNode;
  meta?: React.ReactNode;
  accent?: boolean;
}) => (
  <Flex direction="column" gap="3px" px="14px" py="12px" bg="v2.bg.surface1">
    <Text as="span" textStyle="v2.tableHeader" color="v2.text.subtle">
      {label}
    </Text>
    <Box
      textStyle="numbers.emphasized"
      color={accent ? 'v2.long.default' : 'v2.text.primary'}
    >
      {children}
    </Box>
    {meta != null && (
      <Box textStyle="numbers.micro" color="v2.text.subtle">
        {meta}
      </Box>
    )}
  </Flex>
);

/** Compact-card row: label left, mono value right, hairline dividers */
const LabelRow = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => (
  <Flex
    justify="space-between"
    align="center"
    minH="28px"
    gap="12px"
    borderTop="1px solid"
    borderTopColor="v2.border.hairline"
    _first={{ borderTop: 'none' }}
  >
    <Text as="span" textStyle="v2.label" color="v2.text.muted" flexShrink={0}>
      {label}
    </Text>
    <Box textStyle="numbers.small" color="v2.text.primary" textAlign="right">
      {children}
    </Box>
  </Flex>
);

const GhostLinkButton = ({
  href,
  h,
  children,
}: {
  href: string;
  h: string;
  children: React.ReactNode;
}) => (
  <styled.a
    href={href}
    target="_blank"
    rel="noreferrer"
    display="inline-flex"
    alignItems="center"
    justifyContent="center"
    h={h}
    px="16px"
    borderRadius="2px"
    border="1px solid"
    borderColor="v2.border.default"
    color="v2.text.muted"
    textStyle="v2.label"
    whiteSpace="nowrap"
    cursor="pointer"
    _hover={{ borderColor: 'v2.border.strong', color: 'v2.text.primary' }}
  >
    {children}
  </styled.a>
);

export const CompetitionCard = ({
  competition,
  onSelect,
  onJoin,
  isConnected,
}: CompetitionCardProps) => {
  const chainId = useDataChainId();

  const isUpcoming = competition.status === CompetitionStatus.UPCOMING;
  const isEnded = competition.status === CompetitionStatus.ENDED;
  const isOngoing = !isUpcoming && !isEnded;

  const cta = (() => {
    // Upcoming/Ended competitions are not joinable; keep CTA consistent regardless of connection/join state.
    if (isUpcoming) return { disabled: true, label: 'Soon' } as const;
    if (isEnded) return { disabled: true, label: 'Ended' } as const;
    if (competition.hasJoined)
      return { disabled: true, label: 'Joined' } as const;
    if (!isConnected)
      return { disabled: false, label: 'Connect wallet' } as const;

    return { disabled: false, label: 'Join in' } as const;
  })();

  const rewardDisplay = useMemo(() => {
    if (!competition.rewardAmount) return '-';
    try {
      const raw = BigInt(competition.rewardAmount);
      const decimals = competition.rewardTokenAddress
        ? getErc20TokenByAddress(competition.rewardTokenAddress, chainId)
            ?.decimals ?? 18
        : 18;

      return formatBigValue(raw, {
        decimals,
        isUseSeparator: true,
        isTrimTrailingZero: true,
        autoKmb: false, // Show full number like 2,000,000 instead of 2M
      });
    } catch {
      return '-';
    }
  }, [competition.rewardAmount, competition.rewardTokenAddress]);

  const rewardToken = competition.rewardTokenAddress
    ? getErc20TokenByAddress(competition.rewardTokenAddress, chainId)
    : undefined;
  const rewardSymbol = rewardToken?.symbol || 'USDT';

  const rewardMeta = (
    <HStack gap="4px" alignItems="center">
      {!!rewardToken?.image && (
        <styled.img src={rewardToken.image} w="12px" h="12px" alt="" />
      )}
      <span>{rewardSymbol}</span>
    </HStack>
  );

  const ctaButtons = (h: string) => (
    <Flex gap="8px" mt="auto" pt="2px" flexWrap="wrap">
      {cta.disabled ? (
        <Button variant="secondary" size="small" h={h} px="22px" disabled>
          {cta.label}
        </Button>
      ) : (
        <RequireConnectionClickable>
          <Button
            variant="primary"
            size="small"
            h={h}
            px="22px"
            onClick={() => {
              if (!isConnected) return;
              onJoin?.(competition);
            }}
          >
            {cta.label}
          </Button>
        </RequireConnectionClickable>
      )}
      <Button
        variant="secondary"
        size="small"
        h={h}
        px="16px"
        onClick={() => onSelect?.(competition)}
      >
        View Details
      </Button>
      {!!competition.ruleLink && (
        <GhostLinkButton href={competition.ruleLink} h={h}>
          Event Details
        </GhostLinkButton>
      )}
    </Flex>
  );

  const titleButton = (
    titleTextStyle: React.ComponentProps<typeof Text>['textStyle'],
  ) => (
    <styled.button
      type="button"
      cursor="pointer"
      textAlign="left"
      w="fit-content"
      onClick={() => onSelect?.(competition)}
    >
      <Text
        as="h2"
        textStyle={titleTextStyle}
        color="v2.text.primary"
        transition="color 120ms ease"
        _hover={{ color: 'v2.long.default' }}
      >
        {competition.title}
      </Text>
    </styled.button>
  );

  /* ---- Featured live card (ongoing) ---- */
  if (isOngoing) {
    return (
      <Grid
        bg="v2.bg.panel"
        border="1px solid"
        borderColor="v2.long.border"
        borderRadius="4px"
        overflow="hidden"
        gap={0}
        gridTemplateColumns={{
          base: '1fr',
          desktop: competition.banner
            ? 'minmax(0, 1.05fr) minmax(0, 0.95fr)'
            : '1fr',
        }}
      >
        <Flex
          direction="column"
          gap="14px"
          p={{ base: '16px', desktop: '20px' }}
          borderRight={{
            desktop: competition.banner ? '1px solid' : 'none',
          }}
          borderRightColor={{ desktop: 'v2.border.default' }}
          minW={0}
        >
          <HStack gap="8px" flexWrap="wrap">
            <StatusPill status={competition.status} />
            <KindMeta>{competition.type}</KindMeta>
          </HStack>

          {titleButton({ base: 'styleTitle.default', desktop: 'styleTitle.xlarge' })}

          {!!competition.description && (
            <Text textStyle="text.small" color="v2.text.muted" maxW="60ch">
              {competition.description}
            </Text>
          )}

          <Text
            as="span"
            fontFamily="Inter"
            fontSize="11px"
            fontWeight={500}
            color="v2.text.subtle"
            css={{ fontVariantNumeric: 'tabular-nums' }}
          >
            {formatUtcShort(competition.startTime)} –{' '}
            {formatUtcShort(competition.endTime)} UTC
          </Text>

          <Grid
            gridTemplateColumns="repeat(2, minmax(0, 1fr))"
            gap="1px"
            bg="v2.border.default"
            border="1px solid"
            borderColor="v2.border.default"
            borderRadius="4px"
            overflow="hidden"
          >
            <StatCell label="Prize Pool" meta={rewardMeta}>
              {rewardDisplay}
            </StatCell>
            <StatCell label="Ends In" accent meta="dd:hh:mm:ss">
              <CountdownInline
                targetTime={competition.endTime}
                doneLabel="Ended"
              />
            </StatCell>
            <StatCell label="Traders">
              {competition.currentParticipants.toLocaleString()}
            </StatCell>
            <StatCell label="Volume">
              ${competition.tradingVolume.toLocaleString()}
            </StatCell>
          </Grid>

          {ctaButtons('40px')}
        </Flex>

        {!!competition.banner && (
          <Box
            position="relative"
            minH={{ base: '160px', desktop: '240px' }}
            bg="v2.bg.surface1"
          >
            <styled.img
              src={competition.banner}
              alt={competition.title}
              position="absolute"
              inset={0}
              w="100%"
              h="100%"
              objectFit="cover"
            />
          </Box>
        )}
      </Grid>
    );
  }

  /* ---- Compact card (upcoming / ended) ---- */
  return (
    <Flex
      direction="column"
      gap="12px"
      p="16px"
      bg="v2.bg.panel"
      border="1px solid"
      borderColor="v2.border.default"
      borderRadius="4px"
      h="100%"
      minW={0}
    >
      <HStack gap="8px" flexWrap="wrap">
        <StatusPill status={competition.status} />
        <KindMeta>{competition.type}</KindMeta>
      </HStack>

      {titleButton('styleTitle.small')}

      {!!competition.banner && (
        <Box
          borderRadius="2px"
          overflow="hidden"
          bg="v2.bg.surface1"
          aspectRatio="1144 / 464"
        >
          <styled.img
            src={competition.banner}
            alt={competition.title}
            w="100%"
            h="100%"
            objectFit="cover"
          />
        </Box>
      )}

      {!!competition.description && (
        <Text textStyle="text.small" color="v2.text.muted">
          {competition.description}
        </Text>
      )}

      <Stack gap={0}>
        {isUpcoming ? (
          <>
            <LabelRow label="Starts">
              {formatUtcShort(competition.startTime)} UTC
            </LabelRow>
            <LabelRow label="Ends">
              {formatUtcShort(competition.endTime)} UTC
            </LabelRow>
            <LabelRow label="Prize Pool">
              <HStack gap="6px" justify="flex-end">
                <span>{rewardDisplay}</span>
                {rewardMeta}
              </HStack>
            </LabelRow>
            <LabelRow label="Volume">
              ${competition.tradingVolume.toLocaleString()}
            </LabelRow>
            <LabelRow label="Starts In">
              <CountdownInline
                targetTime={competition.startTime}
                doneLabel="Started"
              />
            </LabelRow>
          </>
        ) : (
          <>
            <LabelRow label="Ended">
              {formatUtcShort(competition.endTime)} UTC
            </LabelRow>
            <LabelRow label="Participants">
              {competition.currentParticipants.toLocaleString()}
            </LabelRow>
            <LabelRow label="Volume">
              ${competition.tradingVolume.toLocaleString()}
            </LabelRow>
            <LabelRow label="Prize Pool">
              <HStack gap="6px" justify="flex-end">
                <span>{rewardDisplay}</span>
                {rewardMeta}
              </HStack>
            </LabelRow>
          </>
        )}
      </Stack>

      {ctaButtons('32px')}
    </Flex>
  );
};
